import React, { useEffect, useState } from 'react'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import { Box, Chip, Collapse, IconButton, Tooltip, Typography, alpha } from '@mui/material'
import { styled } from '@mui/material/styles'

import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '~assets/icons'
import { Icon } from '~components/icons'
import { lightTheme } from '~createTheme'
import {
  type EnrichedOrganizationAISettings,
  type EnrichedToolCall,
  type IGraphNode,
  type IRetrievalResult,
  SourceAccessTypeEnum,
  type SourceDisplayLink,
  SourceType,
  ToolCallStatus,
  type ToolCallStatusEnum,
} from '~types'

import { SourceLink } from './MessageBubble'

interface WebSearchChunk extends SourceDisplayLink {
  chunk_id?: string
  is_real_time_search_result?: boolean
}

interface ToolCallInfoProps {
  toolCall: EnrichedToolCall
  orgAiSettings: EnrichedOrganizationAISettings | null
  showDebugInfo?: boolean
}

const CollapsibleSection = styled(Box)(({ theme }) => ({
  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  marginTop: theme.spacing(1),
  backgroundColor: alpha(theme.palette.grey[100], 0.3),
}))

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 1.5),
  cursor: 'pointer',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  transition: 'background-color 0.2s',
}))

const SectionContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: alpha(theme.palette.background.default, 0.5),
  maxHeight: '400px',
  overflowY: 'auto',
}))

const CodeBlock = styled('pre')(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1.5),
  backgroundColor: alpha(theme.palette.grey[100], 0.5),
  borderRadius: theme.shape.borderRadius,
  overflow: 'auto',
  fontSize: '0.875rem',
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}))

const PageSourcesSection = ({
  show,
  onToggle,
  sources,
  orgAiSettings,
}: {
  show: boolean
  onToggle: () => void
  sources: SourceDisplayLink[]
  orgAiSettings: EnrichedOrganizationAISettings | null
}): JSX.Element => (
  <CollapsibleSection>
    <SectionHeader
      onClick={onToggle}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onToggle()
        }
      }}
      aria-expanded={show}
      aria-label='Toggle page sources'
    >
      <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
        Page Sources
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton size='small' sx={{ p: 0 }}>
          {show ? (
            <ChevronUpIcon color={lightTheme.palette.text.primary} />
          ) : (
            <ChevronDownIcon color={lightTheme.palette.text.primary} />
          )}
        </IconButton>
      </Box>
    </SectionHeader>
    <Collapse in={show}>
      <SectionContent>
        {sources.length > 0 ? (
          sources.map((source, index) => (
            <Box
              key={index}
              sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }, borderRadius: 1, p: 1 }}
            >
              <Box
                className='flex-v-align'
                sx={{ display: 'flex', justifyContent: 'left', gap: 1 }}
              >
                <SourceLink
                  orgAiSettings={orgAiSettings}
                  source={source}
                  linkToInternalPageSource={true}
                />
              </Box>
            </Box>
          ))
        ) : (
          <Typography variant='body2' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No page sources retrieved
          </Typography>
        )}
      </SectionContent>
    </Collapse>
  </CollapsibleSection>
)

const SUCCESS_COLOR = '#4caf50'
const SUCCESS_BG = alpha('#4caf50', 0.1)

const ERROR_COLOR = '#f44336'
const ERROR_BG = alpha('#f44336', 0.08)

const ABANDONED_COLOR = '#ff9800'
const ABANDONED_BG = alpha('#ff9800', 0.08)

export const ToolCallInfo: React.FC<ToolCallInfoProps> = ({ toolCall, orgAiSettings }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showParams, setShowParams] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [showSources, setShowSources] = useState(false)
  const [showEntitySources, setShowEntitySources] = useState(false)
  const [showWebSearchResults, setShowWebSearchResults] = useState(false)
  const [showGraphNodes, setShowGraphNodes] = useState(false)
  const [copiedParams, setCopiedParams] = useState(false)
  const [copiedResults, setCopiedResults] = useState(false)
  const [uniqueSources, setUniqueSources] = useState<SourceDisplayLink[]>([])
  const [uniqueEntitySources, setUniqueEntitySources] = useState<SourceDisplayLink[]>([])
  const [webSearchResults, setWebSearchResults] = useState<WebSearchChunk[]>([])
  const [uniqueGraphNodes, setUniqueGraphNodes] = useState<IGraphNode[]>([])

  useEffect(() => {
    const chunks = ((toolCall.result_json as unknown as IRetrievalResult)?.chunks ??
      []) as WebSearchChunk[]

    // Extract web search results (chunks with is_real_time_search_result === true)
    const webChunks = chunks.filter((chunk) => chunk.is_real_time_search_result === true)
    const uniqueWebChunks = Array.from(
      new Map(webChunks.map((chunk) => [chunk.page_url ?? chunk.chunk_id, chunk])).values(),
    )
    setWebSearchResults(uniqueWebChunks)

    // Build set of web search source IDs for exclusion from page sources
    const webSearchSourceIds = new Set(webChunks.map((chunk) => chunk.source_id))

    const pages = ((toolCall.result_json as unknown as IRetrievalResult)?.pages ??
      []) as SourceDisplayLink[]

    const sources = [...chunks, ...pages]

    const filteredUniqueSources = Array.from(
      new Map(
        sources
          .filter(
            (source) =>
              (source.source_type === 'SourceWebsite' || source.source_type === 'SourceDocument') &&
              !webSearchSourceIds.has(source.source_id),
          )
          .map((source) => [source.source_id, source]),
      ).values(),
    )
    setUniqueSources(filteredUniqueSources)

    // Extract graph nodes from results
    const nodes = (toolCall.result_json as unknown as IRetrievalResult)?.graph_data?.nodes ?? []
    setUniqueGraphNodes(nodes)

    // Extract unique page sources from search_entities results
    const entities =
      (
        toolCall.result_json as unknown as {
          entities?: { page_url?: string; page_title?: string; page_id?: string | null }[]
        }
      )?.entities ?? []
    const uniqueByUrl = Array.from(
      new Map(
        entities
          .filter((e) => e.page_url)
          .map((e) => [
            e.page_url,
            {
              organization_name: '',
              title: e.page_title || e.page_url!,
              source_type: SourceType.SourceWebsite,
              source_access_type: SourceAccessTypeEnum.enum.external,
              is_linked_organization: false,
              source_id: '',
              page_url: e.page_url,
              page_source_id: e.page_id ?? undefined,
            } satisfies SourceDisplayLink,
          ]),
      ).values(),
    )
    setUniqueEntitySources(uniqueByUrl)
  }, [toolCall.result_json])

  const formatDuration = (seconds: number): string => {
    if (seconds < 1) {
      return `${(seconds * 1000).toFixed(0)}ms`
    }
    return `${seconds.toFixed(2)}s`
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formatJson = (data: any): string => {
    if (data === null || data === undefined) {
      return 'null'
    }
    try {
      return JSON.stringify(data, null, 2)
    } catch (_error) {
      return String(data)
    }
  }

  const getStatusColor = (status: ToolCallStatusEnum): string => {
    if (status === ToolCallStatus.enum.completed) return SUCCESS_COLOR
    if (status === ToolCallStatus.enum.abandoned) return ABANDONED_COLOR
    return ERROR_COLOR
  }

  const getStatusBg = (status: ToolCallStatusEnum): string => {
    if (status === ToolCallStatus.enum.completed) return SUCCESS_BG
    if (status === ToolCallStatus.enum.abandoned) return ABANDONED_BG
    return ERROR_BG
  }

  const getStatusIcon = (status: ToolCallStatusEnum): JSX.Element => {
    if (status === ToolCallStatus.enum.completed) return <CheckCircleIcon sx={{ fontSize: 16 }} />
    if (status === ToolCallStatus.enum.abandoned) return <AccessTimeIcon sx={{ fontSize: 16 }} />
    return <ErrorIcon sx={{ fontSize: 16 }} />
  }

  const handleCopy = async (
    data: unknown,
    setCopied: React.Dispatch<React.SetStateAction<boolean>>,
    errorLabel: string,
  ): Promise<void> => {
    try {
      const text = formatJson(data)
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error(`Failed to copy ${errorLabel}:`, err)
    }
  }

  const handleCopyParams = (): Promise<void> =>
    handleCopy(toolCall.params_json, setCopiedParams, 'parameters')

  const handleCopyResults = (): Promise<void> =>
    handleCopy(toolCall.result_json, setCopiedResults, 'results')

  const toggleExpanded = (): void => {
    setIsExpanded(!isExpanded)
    if (isExpanded) {
      setShowParams(false)
      setShowResults(false)
      setShowSources(false)
      setShowWebSearchResults(false)
      setShowGraphNodes(false)
      setShowEntitySources(false)
    }
  }

  const renderCopyButton = (
    tooltipTitle: string,
    isCopied: boolean,
    onClick: () => void,
  ): JSX.Element => (
    <Tooltip title={tooltipTitle}>
      <IconButton
        size='small'
        sx={{ p: 0.5 }}
        onClick={(e) => {
          e.stopPropagation()
          onClick()
        }}
      >
        {isCopied ? <CheckIcon width={16} /> : <Icon name='ContentCopy' size={16} />}
      </IconButton>
    </Tooltip>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        mb: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: '85%',
          width: '100%',
          backgroundColor: 'transparent',
          border: `1px solid ${alpha(lightTheme.palette.divider, 0.2)}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          onClick={toggleExpanded}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              toggleExpanded()
            }
          }}
          role='button'
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-label='Toggle tool call details'
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            cursor: 'pointer',
            backgroundColor: alpha(lightTheme.palette.grey[100], 0.3),
            '&:hover': {
              backgroundColor: alpha(lightTheme.palette.grey[200], 0.4),
            },
            transition: 'background-color 0.2s',
            flexWrap: 'wrap',
          }}
        >
          <BuildOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20, flexShrink: 0 }} />
          <Typography
            variant='body2'
            sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            Tool Call:{' '}
            <Box
              component='code'
              sx={{
                backgroundColor: alpha(lightTheme.palette.grey[300], 0.4),
                padding: '2px 6px',
                borderRadius: '3px',
                fontSize: '0.875rem',
                fontFamily: 'monospace',
              }}
            >
              {toolCall.tool_name || 'unknown'}
            </Box>
          </Typography>
          <Chip
            icon={getStatusIcon(toolCall.status || ToolCallStatus.enum.failed)}
            label={toolCall.status}
            size='small'
            sx={{
              textTransform: 'capitalize',
              backgroundColor: getStatusBg(toolCall.status || ToolCallStatus.enum.failed),
              color: getStatusColor(toolCall.status || ToolCallStatus.enum.failed),
              '& .MuiChip-icon': {
                color: getStatusColor(toolCall.status || ToolCallStatus.enum.failed),
              },
            }}
          />
          <Chip
            icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
            label={formatDuration((toolCall.time_taken_ms ?? 0) / 1000 || 0)}
            size='small'
            sx={{
              bgcolor: alpha(lightTheme.palette.grey[400], 0.15),
              color: 'text.secondary',
            }}
          />
          <IconButton size='small' sx={{ p: 0, ml: 'auto' }}>
            {isExpanded ? (
              <ChevronUpIcon color={lightTheme.palette.text.secondary} />
            ) : (
              <ChevronDownIcon color={lightTheme.palette.text.secondary} />
            )}
          </IconButton>
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{ p: 2, backgroundColor: alpha(lightTheme.palette.background.default, 0.3) }}>
            {toolCall.error_message && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  backgroundColor:
                    toolCall.status === ToolCallStatus.enum.abandoned ? ABANDONED_BG : ERROR_BG,
                  borderRadius: 1,
                  border: `1px solid ${alpha(toolCall.status === ToolCallStatus.enum.abandoned ? ABANDONED_COLOR : ERROR_COLOR, 0.2)}`,
                }}
              >
                <Typography
                  variant='caption'
                  sx={{
                    fontWeight: 'bold',
                    display: 'block',
                    mb: 0.5,

                    color:
                      toolCall.status === ToolCallStatus.enum.abandoned
                        ? ABANDONED_COLOR
                        : ERROR_COLOR,
                  }}
                >
                  {toolCall.status === ToolCallStatus.enum.abandoned ? 'Abandoned:' : 'Error:'}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color:
                      toolCall.status === ToolCallStatus.enum.abandoned
                        ? ABANDONED_COLOR
                        : ERROR_COLOR,
                  }}
                >
                  {toolCall.error_message}
                </Typography>
              </Box>
            )}

            <CollapsibleSection>
              <SectionHeader
                onClick={() => setShowParams(!showParams)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setShowParams(!showParams)
                  }
                }}
                aria-expanded={showParams}
                aria-label='Toggle input parameters'
              >
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  Input Parameters
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {!!toolCall.params_json &&
                    renderCopyButton('Copy input parameters', copiedParams, handleCopyParams)}
                  <IconButton size='small' sx={{ p: 0 }}>
                    {showParams ? (
                      <ChevronUpIcon color={lightTheme.palette.text.primary} />
                    ) : (
                      <ChevronDownIcon color={lightTheme.palette.text.primary} />
                    )}
                  </IconButton>
                </Box>
              </SectionHeader>
              <Collapse in={showParams}>
                <SectionContent>
                  <CodeBlock>{formatJson(toolCall.params_json)}</CodeBlock>
                </SectionContent>
              </Collapse>
            </CollapsibleSection>

            <CollapsibleSection>
              <SectionHeader
                onClick={() => setShowResults(!showResults)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setShowResults(!showResults)
                  }
                }}
                aria-expanded={showResults}
                aria-label='Toggle results'
              >
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  Results
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {!!toolCall.result_json &&
                    renderCopyButton('Copy results', copiedResults, handleCopyResults)}
                  <IconButton size='small' sx={{ p: 0 }}>
                    {showResults ? (
                      <ChevronUpIcon color={lightTheme.palette.text.primary} />
                    ) : (
                      <ChevronDownIcon color={lightTheme.palette.text.primary} />
                    )}
                  </IconButton>
                </Box>
              </SectionHeader>
              <Collapse in={showResults}>
                <SectionContent>
                  {toolCall.result_json ? (
                    <CodeBlock>{formatJson(toolCall.result_json)}</CodeBlock>
                  ) : (
                    <Typography
                      variant='body2'
                      sx={{
                        color: 'text.secondary',
                        fontStyle: 'italic',
                      }}
                    >
                      No results available
                    </Typography>
                  )}
                </SectionContent>
              </Collapse>
            </CollapsibleSection>

            {toolCall.status === ToolCallStatus.enum.completed &&
              toolCall.tool_name === 'retrieve_sources' && (
                <PageSourcesSection
                  show={showSources}
                  onToggle={() => setShowSources(!showSources)}
                  sources={uniqueSources}
                  orgAiSettings={orgAiSettings}
                />
              )}

            {toolCall.status === ToolCallStatus.enum.completed &&
              toolCall.tool_name === 'search_entities' && (
                <PageSourcesSection
                  show={showEntitySources}
                  onToggle={() => setShowEntitySources(!showEntitySources)}
                  sources={uniqueEntitySources}
                  orgAiSettings={orgAiSettings}
                />
              )}

            {toolCall.status === ToolCallStatus.enum.completed &&
              toolCall.tool_name === 'retrieve_sources' &&
              webSearchResults.length > 0 && (
                <CollapsibleSection>
                  <SectionHeader
                    onClick={() => setShowWebSearchResults(!showWebSearchResults)}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setShowWebSearchResults(!showWebSearchResults)
                      }
                    }}
                    aria-expanded={showWebSearchResults}
                    aria-label='Toggle web search results'
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Icon
                        name='TravelExploreIcon'
                        size={18}
                        color={lightTheme.palette.primary.main}
                      />
                      <Typography
                        variant='body2'
                        sx={{
                          fontWeight: 'bold',
                        }}
                      >
                        Web Search Results
                      </Typography>
                      <Chip
                        label={webSearchResults.length}
                        size='small'
                        sx={{
                          height: 20,
                          minWidth: 20,
                          fontSize: '0.75rem',
                          bgcolor: alpha(lightTheme.palette.primary.main, 0.1),
                          color: lightTheme.palette.primary.main,
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton size='small' sx={{ p: 0 }}>
                        {showWebSearchResults ? (
                          <ChevronUpIcon color={lightTheme.palette.text.primary} />
                        ) : (
                          <ChevronDownIcon color={lightTheme.palette.text.primary} />
                        )}
                      </IconButton>
                    </Box>
                  </SectionHeader>
                  <Collapse in={showWebSearchResults}>
                    <SectionContent>
                      {webSearchResults.map((result, index) => (
                        <Box
                          key={index}
                          sx={{
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                            borderRadius: 1,
                            p: 1,
                            mb: 0.5,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Icon
                              name='TravelExploreIcon'
                              size={16}
                              color={lightTheme.palette.primary.main}
                            />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                variant='body2'
                                sx={{
                                  fontWeight: 'medium',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {result.title || 'Untitled'}
                              </Typography>
                              {result.page_url && (
                                <Typography
                                  variant='caption'
                                  component='a'
                                  href={result.page_url}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  sx={{
                                    color: lightTheme.palette.primary.main,
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                  }}
                                >
                                  {result.page_url}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </SectionContent>
                  </Collapse>
                </CollapsibleSection>
              )}
            {toolCall.status === ToolCallStatus.enum.completed && uniqueGraphNodes.length > 0 && (
              <CollapsibleSection>
                <SectionHeader
                  onClick={() => setShowGraphNodes(!showGraphNodes)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setShowGraphNodes(!showGraphNodes)
                    }
                  }}
                  aria-expanded={showGraphNodes}
                  aria-label='Toggle graph nodes'
                >
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 'bold',
                    }}
                  >
                    Graph Nodes
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton size='small' sx={{ p: 0 }}>
                      {showGraphNodes ? (
                        <ChevronUpIcon color={lightTheme.palette.text.primary} />
                      ) : (
                        <ChevronDownIcon color={lightTheme.palette.text.primary} />
                      )}
                    </IconButton>
                  </Box>
                </SectionHeader>
                <Collapse in={showGraphNodes}>
                  <SectionContent>
                    {uniqueGraphNodes.slice(0, 10).map((node, index) => (
                      <Box key={index} sx={{ p: 0.5 }}>
                        <Typography variant='body2'>
                          Node: {node.name} ({node.labels.join(', ')})
                        </Typography>
                      </Box>
                    ))}
                    {uniqueGraphNodes.length > 10 && (
                      <Typography
                        variant='body2'
                        sx={{
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          p: 0.5,
                        }}
                      >
                        ... and {uniqueGraphNodes.length - 10} more nodes were retrieved
                      </Typography>
                    )}
                  </SectionContent>
                </Collapse>
              </CollapsibleSection>
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  )
}
